import {
  CONTACT,
  FAQS,
  FEATURES,
  PRICING,
  USECASES,
  SIGNIN,
  SIGNOUT,
  SIGNUP,
} from "@constants/unicomply-keys";
import UniComplyLogo from "@icons/UniComplyLogo";
import { Box, Button, styled } from "@mui/material";
import React from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { useNavigate, useLocation } from "react-router-dom";
import { isLocalAuthenticated, logoutLocal } from "@services/auth";
import { getAppPath, landingVerticalFromPath } from "@utils/appPath";

const NavigationContainer = styled(Box)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.6)",
  backdropFilter: "blur(8px)",
  borderRadius: "16px",
  flex: 1,
  maxWidth: 1000,
  marginLeft: 14,
  marginRight: 14, // Add matching right margin
  boxShadow:
    "0px 8px 32px rgba(195, 244, 248, 0.6), 0px 3px 15px rgba(230, 253, 255, 0.3)",
  // border: "2px solid rgba(255, 255, 255, 0.2)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 20px",
  height: 56,
  [theme.breakpoints.down("lg")]: {
    maxWidth: "unset",
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "stretch",
    height: "auto",
    padding: "8px 12px",
    marginLeft: 0,
    marginRight: 32, // Reset margins on mobile
    gap: 12,
    width: "100%",
  },
}));

const NavButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  fontSize: 16,
  letterSpacing: "-0.4px",
  lineHeight: "22px",
  color: "#033843",
  minWidth: "auto",
  borderRadius: 0,
  whiteSpace: "nowrap", // Prevent text wrapping
  // remove ALL background changes
  backgroundColor: "transparent !important",

  "&:hover": {
    backgroundColor: "transparent !important",
    boxShadow: "none",
    borderBottom: "2px solid #35D7E4",
  },
  "&:active": {
    backgroundColor: "transparent !important",
    boxShadow: "none",
  },
  "&:focus": {
    backgroundColor: "transparent !important",
    boxShadow: "none",
  },
  "&.Mui-focusVisible": {
    backgroundColor: "transparent !important",
    background: "none !important",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: 14,
    whiteSpace: "normal", // Allow wrapping on very small screens
    wordBreak: "break-word", // Break long words if needed
  },
}));

interface NavItemProps {
  text: string;
  classProperty: string;
  href?: string;
  onClick?: () => void;
}

function NavItem({ text, classProperty, href = "#", onClick }: NavItemProps) {
  return (
    <NavButton
      className={classProperty}
      onClick={onClick}
      disableRipple
      disableElevation
      disableFocusRipple
    >
      {text}
    </NavButton>
  );
}

const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "colorVariant",
})<{ colorVariant: "primary" | "secondary" }>(
  ({ colorVariant }: { colorVariant: any }) => ({
    height: 30,
    padding: "4px 12px",
    borderRadius: 8,
    fontWeight: 500,
    fontSize: 14,
    letterSpacing: "-0.4px",
    lineHeight: "22px",
    textTransform: "none",
    ...(colorVariant === "primary"
      ? {
          backgroundColor: "#065b70",
          color: "#e6fafc",
          boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.1)",
          // "&:hover": {
          //   // backgroundColor: "#05495a",
          //   boxShadow: "none", // prevent shadow on hover
          //   backgroundColor: "#065b70", // optional: avoid grey hover background
          // },
        }
      : {
          color: "#1a1a1a",
          "&:hover": {
            // backgroundColor: "rgba(255, 255, 255, 0.1)",
            boxShadow: "none", // prevent shadow on hover
            backgroundColor: "transparent",
          },
        }),
  })
);

const LogoContainer = styled(Box)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.6)",
  backdropFilter: "blur(8px)",
  borderRadius: "16px",
  width: 200,
  height: 56,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px 20px",
  gap: 8,
  border: "2px solid rgba(247, 247, 247, 0.20)",
  boxShadow:
    "0 8px 32px 0 rgba(195, 244, 248, 0.60), 0 3px 15.5px 0 rgba(230, 253, 255, 0.30)",
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    width: "auto",
    padding: "8px 12px",
    gap: 6,
  },
}));

const LogoText = styled(Box)(({ theme }) => ({
  fontWeight: 700,
  fontSize: 20,
  lineHeight: "22px",
  letterSpacing: "-0.4px",
  display: "flex",
  alignItems: "center",
  gap: 8,
  [theme.breakpoints.down("sm")]: {
    fontSize: 18,
  },
}));

const LogoIcon = styled("div")(({ theme }) => ({
  // width: 49,
  height: 46,
  [theme.breakpoints.down("sm")]: {
    width: 40,
    height: 38,
  },
}));
export default function UniComply_Header() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const location = useLocation();
  const landingVertical = landingVerticalFromPath(getAppPath(location));

  const authPathWithVertical = (basePath: string) => {
    if (!landingVertical) return basePath;
    const params = new URLSearchParams({ vertical: landingVertical });
    return `${basePath}?${params.toString()}`;
  };

  const handleLogin = async () => {
    try {
      navigate(authPathWithVertical("/login"));
    } catch (e) {
      console.error(e);
    }
  };

  const handleLogout = () => {
    const hasSsoSession = isAuthenticated && accounts && accounts.length > 0;
    const target =
      process.env.REACT_APP_POST_LOGOUT_REDIRECT_URL || window.location.origin;

    if (hasSsoSession) {
      instance.logoutRedirect({
        postLogoutRedirectUri: target,
      });
    } else {
      logoutLocal()
        .catch(() => {
          // ignore cleanup errors
        })
        .finally(() => {
          // Hard redirect to avoid any SPA routing quirks in production
          window.location.replace(target);
        });
    }
  };

  const handleNavClick = (section: string) => {
    const element = document.getElementById(section.toLowerCase());
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };
  return (
    <Box sx={{ position: "relative", zIndex: 20 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.5, md: 1.75 },
          maxWidth: "1200px",
          margin: "0 auto",
          padding: { xs: "12px", md: "16px" },
          flexDirection: { xs: "column", md: "row" },
          width: "100%",
        }}
      >
        {/* Logo Section */}
        <LogoContainer>
          <LogoText>
            <LogoIcon>
              <UniComplyLogo />
            </LogoIcon>
          </LogoText>
        </LogoContainer>
        {/* Navigation Section */}
        <NavigationContainer>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2, md: 5 },
              flexWrap: { xs: "wrap", md: "nowrap" },
              justifyContent: { xs: "center", md: "flex-start" },
              minHeight: { xs: "auto", sm: "40px" },
            }}
          >
            <NavItem
              classProperty="defaultclass"
              text={FEATURES}
              onClick={() => handleNavClick("features")}
            />
            {}
            <NavItem
              classProperty="defaultclass"
              text={USECASES}
              onClick={() => handleNavClick("use-cases")}
            />
            <NavItem
              text={PRICING}
              classProperty="defaultclass"
              onClick={() => handleNavClick("pricing")}
            />
            <NavItem
              text={FAQS}
              classProperty="defaultclass"
              onClick={() => handleNavClick("faqs")}
            />
            <NavItem
              text={CONTACT}
              classProperty="defaultclass"
              onClick={() => handleNavClick("contact")}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.75, md: 1 },
              justifyContent: { xs: "center", md: "flex-end" },
              flexWrap: { xs: "wrap", md: "nowrap" },
            }}
          >
            {!isLocalAuthenticated() ? (
              <>
                <ActionButton colorVariant="secondary" onClick={handleLogin}>
                  {SIGNIN}
                </ActionButton>
                <ActionButton
                  colorVariant="primary"
                  onClick={() =>
                    navigate(
                      landingVertical && landingVertical !== "healthcare"
                        ? authPathWithVertical("/signup-request")
                        : authPathWithVertical("/signup"),
                    )
                  }
                >
                  {SIGNUP}
                </ActionButton>
              </>
            ) : (
              <ActionButton colorVariant="primary" onClick={handleLogout}>
                {SIGNOUT}
              </ActionButton>
            )}
          </Box>
        </NavigationContainer>
      </Box>
    </Box>
  );
}
